import './unitary-target-selection.ajscss';

import { isEmailValid } from '@agent/core/channel-digit/mail/util/mail-util';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ICustomerCardsDeprecatedService } from '@commons/customer/customer-cards-deprecated.service';
import { IContact } from '@commons/models/contact';
import { IComponentController, IComponentOptions, ILogService, isDefined, isUndefined } from 'angular';

import { ChannelType, PhoneValidationService } from '@contact/upgrade';

import templateUrl from './unitary-target-selection.html';

class UnitaryTargetSelectionCtrl implements IComponentController {
  /**
   * parent variable binded to : containing the target uri
   */
  public uriValid: boolean;
  /**
   * to avoid flash display of error message
   */
  public initDone = false;
  /**
   * selectable target types
   */
  private readonly availableTargetTypeList: Array<string> = ['CUSTOMER_CARD', 'MANUAL_ENTRY'];
  /**
   * current target type selected
   */
  private selectedTargetType: string;
  /**
   * list of uris available for given media of given contact
   */
  private contactAvailableUriList: Array<string> = [];
  /**
   * input param : if we want to preselect an address of the given contact id
   * if not valued we will preselect first address of given media
   */
  private readonly defaultUri: string;
  /**
   * input param : channel type as a string
   */
  private readonly media: string;
  /**
   * optional input param : id of the response of contact (optional) : used to show available addresses of the contact with the given media
   */
  private readonly responseId: string;
  /**
   * optional input param : id of the contact (optional) : used to show available addresses of the contact with the given media
   */
  private readonly contactId: string;
  /**
   * parent variable binded to : containing the target uri
   */
  private uri: string;
  /**
   * if the uri can be validated and is valid
   * always true if media is not supported / valued
   */
  private manualEntryValid = true;
  /**
   * current channel type to filter contact addresses if contact id is valued
   */
  private internalMedia: ChannelType;
  /**
   * parent variable binded to : if true display star after label
   */
  private mandatoryLabel: string;

  /**
   * constructor
   * @param $log
   * @param $i18next
   * @param contactDeprecatedService
   * @param phoneValidation
   */
  constructor(
    private readonly $log: ILogService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly customerCardService: ICustomerCardsDeprecatedService,
    private readonly phoneValidation: PhoneValidationService
  ) {}

  $onInit(): void {
    // convert string to channel type
    this.internalMedia = ChannelType[this.media];
    // handle unitary phone as phone
    if (this.internalMedia === ChannelType.UNITARY_PHONE) {
      this.internalMedia = ChannelType.PHONE;
    }
    if ('true' !== this.mandatoryLabel) {
      this.mandatoryLabel = 'false';
    }
    // if we have contact id
    if (this.contactId != null && this.contactId !== '') {
      this.selectedTargetType = this.availableTargetTypeList[0];
      this.customerCardService
        .getCustomerCard(this.contactId)
        .then(customer => {
          if (!customer) {
            throw new Error();
          }
          return customer;
        })
        .then(({ addresses }) => {
          this.contactAvailableUriList = addresses
            .filter(address => address.channel.type === this.internalMedia)
            .map(address => address.uri);
        })
        .then(() => this.computeUriPreselected())
        .then(() => {
          this.initDone = true;
        });
    } // if we have response id
    else if (this.responseId != null && this.responseId !== '') {
      this.selectedTargetType = this.availableTargetTypeList[0];
      this.uriValid = true;
      this.contactDeprecatedService.getContact(this.responseId).then((contact: IContact) => {
        this.$log.debug('[unitary-target-selection]got contact from response id[' + this.responseId + ']');
        if (!!contact.customerId) {
          this.customerCardService
            .getCustomerCard(contact.customerId)
            .then(customer => {
              if (!customer) throw new Error();
              return customer;
            })
            .then(({ addresses }) => {
              this.contactAvailableUriList = addresses
                .filter(address => address.channel.type === this.internalMedia)
                .map(address => address.uri);
            })
            .then(() => this.computeUriPreselected())
            .then(() => {
              this.initDone = true;
            });
        }
      });
    } else {
      // manual entry is selected and will not be able to change it
      this.selectedTargetType = this.availableTargetTypeList[1];
      // if we have a default uri use value to populate default value
      if (isUndefined(this.defaultUri) || this.defaultUri === '') {
        this.uri = this.defaultUri;
      }
      this.initDone = true;
    }
  }

  public typeChanged(): void {
    if (this.selectedTargetType === this.availableTargetTypeList[0] && this.contactAvailableUriList.length > 0) {
      this.uri = this.contactAvailableUriList[0];
      this.uriValid = true;
    } else {
      this.uri = '';
      this.uriValid = false;
    }
  }

  checkManualEntry(): void {
    if (isUndefined(this.uri)) {
      this.manualEntryValid = false;
    } else if (this.internalMedia === ChannelType.PHONE) {
      this.uri = this.trim(this.uri);
      if (this.uri !== '') {
        this.manualEntryValid = this.phoneValidation.isValid(this.uri);
      } else {
        this.manualEntryValid = false;
      }
    } else if (this.internalMedia === ChannelType.MAIL) {
      this.uri = this.trim(this.uri);
      if (this.uri !== '') {
        this.manualEntryValid = isEmailValid(this.uri);
      } else {
        this.manualEntryValid = false;
      }
    }
    this.uriValid = this.manualEntryValid;
  }

  /**
   * used to reselect uri according to defaultUri / contactAvailableUriList
   */
  private computeUriPreselected() {
    // retrieve value for param
    const uriToReselect: string = this.defaultUri;
    // try to reselect wanted default uri or first of available contact list if not set
    if (isDefined(uriToReselect) && uriToReselect !== '' && this.contactAvailableUriList.length > 0) {
      const matchingUris: Array<string> = this.contactAvailableUriList.filter(uri => uri === uriToReselect);
      // found
      if (matchingUris.length === 1) {
        this.uri = matchingUris[0];
      } else {
        this.uri = this.contactAvailableUriList[0];
      }
      this.uriValid = true;
    } else if (this.contactAvailableUriList.length > 0) {
      this.uri = this.contactAvailableUriList[0];
      this.uriValid = true;
    } else {
      this.uriValid = false;
    }
  }

  private trim(s: string): string {
    let returnString = '';
    // Search through string's characters one by one.
    // If character is not a whitespace, append to returnString.
    for (let i = 0; i < s.length; i++) {
      // Check that current character isn't whitespace.
      const c = s.charAt(i);
      if (c !== ' ') {
        returnString += c;
      }
    }
    return returnString;
  }
}

export class UnitaryTargetSelection implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    media: '@?',
    responseId: '@?',
    contactId: '@?',
    defaultUri: '@?',
    mandatoryLabel: '@?',
    uri: '=',
    uriValid: '=',
  } as { [binding: string]: string };
  controller = ['$log', 'contactDeprecatedService', 'customerCardsDeprecatedService', 'phoneValidation', UnitaryTargetSelectionCtrl];
}

export default 'unitaryTargetSelection';
